import * as THREE from 'three';

const GRAVITY = -22;
const AIR_DAMP = 0.97;
const FLOOR_DAMP = 0.78;
const SLEEP_TIME = 2.1;
const SLEEP_SPEED = 0.18;
const SOLVE_ITERS = 8;
const MAX_H_SPEED = 4.2;
const MAX_FALL = 14;

const RAGDOLL_BONES = [
  'Hips',
  'Spine',
  'Spine1',
  'Spine2',
  'Neck',
  'Head',
  'LeftUpLeg',
  'LeftLeg',
  'LeftFoot',
  'RightUpLeg',
  'RightLeg',
  'RightFoot',
  'LeftArm',
  'LeftForeArm',
  'LeftHand',
  'RightArm',
  'RightForeArm',
  'RightHand',
] as const;

const WRITE_ORDER = RAGDOLL_BONES;

const LINKS: Array<[string, string]> = [
  ['Hips', 'Spine'],
  ['Spine', 'Spine1'],
  ['Spine1', 'Spine2'],
  ['Spine2', 'Neck'],
  ['Neck', 'Head'],
  ['Hips', 'LeftUpLeg'],
  ['LeftUpLeg', 'LeftLeg'],
  ['LeftLeg', 'LeftFoot'],
  ['Hips', 'RightUpLeg'],
  ['RightUpLeg', 'RightLeg'],
  ['RightLeg', 'RightFoot'],
  ['Spine2', 'LeftArm'],
  ['LeftArm', 'LeftForeArm'],
  ['LeftForeArm', 'LeftHand'],
  ['Spine2', 'RightArm'],
  ['RightArm', 'RightForeArm'],
  ['RightForeArm', 'RightHand'],
];

/** Primary +Y child used to aim each bone. Hips aims at the spine, not a leg. */
const AIM_CHILD: Record<string, string> = {
  Hips: 'Spine',
  Spine: 'Spine1',
  Spine1: 'Spine2',
  Spine2: 'Neck',
  Neck: 'Head',
  LeftUpLeg: 'LeftLeg',
  LeftLeg: 'LeftFoot',
  RightUpLeg: 'RightLeg',
  RightLeg: 'RightFoot',
  LeftArm: 'LeftForeArm',
  LeftForeArm: 'LeftHand',
  RightArm: 'RightForeArm',
  RightForeArm: 'RightHand',
};

const LIMBS: Array<[string, string, string]> = [
  ['Hips', 'LeftUpLeg', 'LeftLeg'],
  ['LeftUpLeg', 'LeftLeg', 'LeftFoot'],
  ['Hips', 'RightUpLeg', 'RightLeg'],
  ['RightUpLeg', 'RightLeg', 'RightFoot'],
  ['Spine2', 'LeftArm', 'LeftForeArm'],
  ['LeftArm', 'LeftForeArm', 'LeftHand'],
  ['Spine2', 'RightArm', 'RightForeArm'],
  ['RightArm', 'RightForeArm', 'RightHand'],
];

interface Particle {
  name: string;
  bone: THREE.Object3D;
  pos: THREE.Vector3;
  prev: THREE.Vector3;
  radius: number;
  side: THREE.Vector3;
  lastQ: THREE.Quaternion;
}

interface Link {
  a: number;
  b: number;
  rest: number;
  stiff: number;
}

interface BendJoint {
  a: number;
  b: number;
  c: number;
  dir: THREE.Vector3;
}

export interface EnemyRagdoll {
  particles: Particle[];
  links: Link[];
  bends: BendJoint[];
  index: Map<string, number>;
  age: number;
  sleeping: boolean;
  stillT: number;
  origin: THREE.Vector3;
  maxSlide: number;
  floorY: number;
}

const _delta = new THREE.Vector3();
const _world = new THREE.Vector3();
const _inv = new THREE.Matrix4();
const _quat = new THREE.Quaternion();
const _pos = new THREE.Vector3();
const _scl = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _y = new THREE.Vector3();
const _x = new THREE.Vector3();
const _z = new THREE.Vector3();
const _mid = new THREE.Vector3();
const _push = new THREE.Vector3();
const _axis = new THREE.Vector3();
const _basis = new THREE.Matrix4();
const _worldQ = new THREE.Quaternion();
const _parentQ = new THREE.Quaternion();

function bare(name: string): string {
  return name.replace(/^mixamorig:/i, '').replace(/^mixamorig/i, '');
}

function collectBones(root: THREE.Object3D): Map<string, THREE.Object3D> {
  const map = new Map<string, THREE.Object3D>();
  root.traverse((o) => {
    const n = bare(o.name);
    if ((RAGDOLL_BONES as readonly string[]).includes(n) && !map.has(n)) map.set(n, o);
  });
  return map;
}

function particleRadius(name: string): number {
  if (name === 'Head') return 0.12;
  if (name === 'Hips') return 0.13;
  if (name.startsWith('Spine')) return 0.11;
  if (name === 'Neck') return 0.08;
  if (name.endsWith('Foot')) return 0.05;
  if (name.endsWith('Hand')) return 0.045;
  if (name.endsWith('UpLeg') || name.endsWith('Arm')) return 0.07;
  return 0.06;
}

function impulseWeight(name: string): number {
  if (name.endsWith('Foot') || name.endsWith('Leg')) return 0.32;
  if (name.endsWith('UpLeg')) return 0.5;
  if (name === 'Head' || name === 'Neck') return 1.35;
  if (name.endsWith('Hand') || name.endsWith('ForeArm')) return 1.05;
  if (name.startsWith('Spine')) return 1.0;
  return 1;
}

export function createRagdoll(
  model: THREE.Object3D,
  maxSlide = 1.15,
  floorY = 0,
): EnemyRagdoll | null {
  model.updateMatrixWorld(true);
  const bones = collectBones(model);
  const hips = bones.get('Hips');
  if (!hips) return null;

  const particles: Particle[] = [];
  const index = new Map<string, number>();
  for (const name of RAGDOLL_BONES) {
    const bone = bones.get(name);
    if (!bone) continue;
    bone.getWorldPosition(_world);
    bone.getWorldQuaternion(_quat);
    index.set(name, particles.length);
    particles.push({
      name,
      bone,
      pos: _world.clone(),
      prev: _world.clone(),
      radius: particleRadius(name),
      side: new THREE.Vector3(1, 0, 0).applyQuaternion(_quat).normalize(),
      lastQ: bone.quaternion.clone(),
    });
  }

  const links: Link[] = [];
  for (const [aName, bName] of LINKS) {
    const a = index.get(aName);
    const b = index.get(bName);
    if (a === undefined || b === undefined) continue;
    const rest = particles[a].pos.distanceTo(particles[b].pos);
    if (rest < 1e-4) continue;
    links.push({ a, b, rest, stiff: 1 });
  }

  const extra: Array<[string, string, number]> = [
    ['Hips', 'Spine2', 1],
    ['Spine', 'Neck', 1],
    ['Spine2', 'Head', 0.9],
    ['Hips', 'Head', 0.7],
    ['LeftUpLeg', 'RightUpLeg', 0.9],
    ['LeftArm', 'RightArm', 0.35],
  ];
  for (const [aName, bName, stiff] of extra) {
    const a = index.get(aName);
    const b = index.get(bName);
    if (a === undefined || b === undefined) continue;
    const rest = particles[a].pos.distanceTo(particles[b].pos);
    if (rest < 1e-4) continue;
    links.push({ a, b, rest, stiff });
  }

  const bends: BendJoint[] = [];
  for (const names of LIMBS) {
    const ia = index.get(names[0]);
    const ib = index.get(names[1]);
    const ic = index.get(names[2]);
    if (ia === undefined || ib === undefined || ic === undefined) continue;
    const a = particles[ia];
    const b = particles[ib];
    const c = particles[ic];
    _axis.subVectors(c.pos, a.pos);
    const span = _axis.lengthSq();
    if (span < 1e-6) continue;
    const t = THREE.MathUtils.clamp(_delta.subVectors(b.pos, a.pos).dot(_axis) / span, 0.15, 0.85);
    _mid.copy(a.pos).addScaledVector(_axis, t);
    _push.subVectors(b.pos, _mid);
    if (_push.lengthSq() < 1e-6) {
      _push.crossVectors(_axis, _up);
      if (_push.lengthSq() < 1e-8) _push.set(0, 1, 0);
    }
    bends.push({ a: ia, b: ib, c: ic, dir: _push.normalize().clone() });
  }

  if (particles.length < 6 || links.length < 4) return null;
  const origin = particles[index.get('Hips') ?? 0].pos.clone();
  return {
    particles,
    links,
    bends,
    index,
    age: 0,
    sleeping: false,
    stillT: 0,
    origin,
    maxSlide,
    floorY,
  };
}

export function addRagdollImpulse(
  rag: EnemyRagdoll,
  dir: THREE.Vector3,
  hitPoint: THREE.Vector3 | undefined,
  strength: number,
): void {
  const n = dir.lengthSq() > 1e-8 ? dir.clone().normalize() : new THREE.Vector3(0, 0, -1);
  for (const p of rag.particles) {
    let w = impulseWeight(p.name);
    if (hitPoint) {
      const d = p.pos.distanceTo(hitPoint);
      w *= 1 + Math.max(0, 1.4 - d) * 0.5;
    }
    const kick = strength * 0.028 * w;
    p.prev.x -= n.x * kick;
    p.prev.y -= kick * 0.12;
    p.prev.z -= n.z * kick;
  }
}

export function stepRagdollPhysics(rag: EnemyRagdoll, dt: number): void {
  if (rag.sleeping) return;
  const t = Math.min(dt, 0.033);
  rag.age += t;

  let maxSpeed = 0;
  for (const p of rag.particles) {
    let vx = (p.pos.x - p.prev.x) * AIR_DAMP;
    let vy = (p.pos.y - p.prev.y) * AIR_DAMP;
    let vz = (p.pos.z - p.prev.z) * AIR_DAMP;
    const onFloor = p.pos.y <= rag.floorY + p.radius + 0.02;
    if (onFloor) {
      vx *= FLOOR_DAMP;
      vz *= FLOOR_DAMP;
    }
    const hSpd = Math.hypot(vx, vz);
    if (hSpd > MAX_H_SPEED * t) {
      const s = (MAX_H_SPEED * t) / hSpd;
      vx *= s;
      vz *= s;
    }
    if (vy < -MAX_FALL * t) vy = -MAX_FALL * t;
    p.prev.copy(p.pos);
    p.pos.x += vx;
    p.pos.y += vy + GRAVITY * t * t;
    p.pos.z += vz;
    collideFloor(p, rag.floorY);
    clampSlide(p, rag);
    maxSpeed = Math.max(maxSpeed, Math.hypot(vx, vy, vz) / Math.max(t, 1e-4));
  }

  for (let i = 0; i < SOLVE_ITERS; i++) {
    for (const link of rag.links) {
      const a = rag.particles[link.a];
      const b = rag.particles[link.b];
      _delta.subVectors(b.pos, a.pos);
      const dist = _delta.length();
      if (dist < 1e-6) continue;
      const diff = ((dist - link.rest) / dist) * 0.5 * link.stiff;
      a.pos.addScaledVector(_delta, diff);
      b.pos.addScaledVector(_delta, -diff);
    }
    for (const joint of rag.bends) keepJointBent(rag, joint);
    for (const p of rag.particles) collideFloor(p, rag.floorY);
  }

  for (const p of rag.particles) {
    clampSlide(p, rag);
    collideFloor(p, rag.floorY);
  }

  const hips = ragdollHips(rag);
  const down = hips.y <= rag.floorY + 0.72;
  if (maxSpeed < SLEEP_SPEED) rag.stillT += t;
  else rag.stillT = 0;
  if ((down && rag.stillT > 0.4) || rag.age >= SLEEP_TIME) {
    rag.sleeping = true;
  }
}

export function writeRagdollBones(rag: EnemyRagdoll): void {
  for (const p of rag.particles) {
    if (!Number.isFinite(p.pos.x + p.pos.y + p.pos.z)) return;
  }

  // Keep authored bone lengths so the body falls instead of crumpling into a pile
  // (which showed backfaces on FrontSide clothes / legs).
  for (const p of rag.particles) {
    const bindP = p.bone.userData.bindP as THREE.Vector3 | undefined;
    if (bindP) p.bone.position.copy(bindP);
  }

  const hipsI = rag.index.get('Hips');
  if (hipsI !== undefined) {
    const hips = rag.particles[hipsI];
    const par = hips.bone.parent;
    if (par) {
      par.updateWorldMatrix(true, false);
      _inv.copy(par.matrixWorld).invert();
      _world.copy(hips.pos).applyMatrix4(_inv);
      if (Number.isFinite(_world.x + _world.y + _world.z)) hips.bone.position.copy(_world);
    }
  }

  for (const name of WRITE_ORDER) {
    const i = rag.index.get(name);
    if (i === undefined) continue;
    const p = rag.particles[i];
    const bone = p.bone;
    const par = bone.parent;
    if (!par) continue;
    par.updateWorldMatrix(true, false);

    const childName = AIM_CHILD[name];
    const childI = childName ? rag.index.get(childName) : undefined;
    if (childI !== undefined) {
      orientBoneY(p, rag.particles[childI].pos);
    } else {
      const incoming = parentLinkName(name);
      const pi = incoming ? rag.index.get(incoming) : undefined;
      if (pi !== undefined) {
        _delta.subVectors(p.pos, rag.particles[pi].pos);
        _world.copy(p.pos).add(_delta);
        orientBoneY(p, _world);
      }
    }
    bone.updateMatrix();
    bone.updateWorldMatrix(true, false);
    bone.getWorldQuaternion(_quat);
    _x.set(1, 0, 0).applyQuaternion(_quat);
    if (_x.lengthSq() > 1e-8) {
      _x.normalize();
      if (_x.dot(p.side) < 0) _x.negate();
      p.side.copy(_x);
    }
  }
}

function parentLinkName(child: string): string | null {
  for (const [a, b] of LINKS) {
    if (b === child) return a;
  }
  return null;
}

/** Proc bones use +Y toward the child. */
function orientBoneY(parent: Particle, childPos: THREE.Vector3): void {
  _y.subVectors(childPos, parent.pos);
  if (_y.lengthSq() < 1e-8) return;
  _y.normalize();

  _x.copy(parent.side);
  _x.addScaledVector(_y, -_x.dot(_y));
  if (_x.lengthSq() < 1e-6) {
    _x.crossVectors(_y, _up.set(0, 1, 0));
    if (_x.lengthSq() < 1e-8) _x.crossVectors(_y, _up.set(1, 0, 0));
  }
  _x.normalize();
  // A 180° roll around the bone shows the back of FrontSide boxes (missing faces).
  if (_x.dot(parent.side) < 0) _x.negate();
  _z.crossVectors(_x, _y).normalize();
  _x.crossVectors(_y, _z).normalize();
  _basis.makeBasis(_x, _y, _z);
  _worldQ.setFromRotationMatrix(_basis);

  const bone = parent.bone;
  const par = bone.parent;
  if (!par) return;
  par.matrixWorld.decompose(_pos, _parentQ, _scl);
  _quat.copy(_parentQ).invert().multiply(_worldQ);
  if (_quat.dot(parent.lastQ) < 0) {
    _quat.x = -_quat.x;
    _quat.y = -_quat.y;
    _quat.z = -_quat.z;
    _quat.w = -_quat.w;
  }
  bone.quaternion.copy(_quat);
  parent.lastQ.copy(_quat);
}

function collideFloor(p: Particle, floorY: number): void {
  const floor = floorY + p.radius;
  if (p.pos.y >= floor) return;
  const vy = p.pos.y - p.prev.y;
  p.pos.y = floor;
  p.prev.y = floor - Math.abs(vy) * 0.08;
  p.prev.x = p.pos.x - (p.pos.x - p.prev.x) * 0.55;
  p.prev.z = p.pos.z - (p.pos.z - p.prev.z) * 0.55;
}

function keepJointBent(rag: EnemyRagdoll, joint: BendJoint): void {
  const a = rag.particles[joint.a];
  const b = rag.particles[joint.b];
  const c = rag.particles[joint.c];
  _axis.subVectors(c.pos, a.pos);
  const span = _axis.lengthSq();
  if (span < 1e-6) return;
  _delta.subVectors(b.pos, a.pos);
  const t = THREE.MathUtils.clamp(_delta.dot(_axis) / span, 0.18, 0.78);
  _mid.copy(a.pos).addScaledVector(_axis, t);
  _push.subVectors(b.pos, _mid);
  const off = _push.length();
  const minOff = 0.08;
  if (off >= minOff) {
    if (off > 1e-5) joint.dir.lerp(_push.normalize(), 0.08).normalize();
    return;
  }
  if (off < 1e-5) _push.copy(joint.dir);
  if (_push.lengthSq() < 1e-8) {
    _push.crossVectors(_axis, _up.set(0, 1, 0));
    if (_push.lengthSq() < 1e-8) _push.set(0, 1, 0);
  }
  _push.normalize();
  b.pos.addScaledVector(_push, minOff - off);
}

function clampSlide(p: Particle, rag: EnemyRagdoll): void {
  const dx = p.pos.x - rag.origin.x;
  const dz = p.pos.z - rag.origin.z;
  const slide = Math.hypot(dx, dz);
  if (slide <= rag.maxSlide) return;
  const k = rag.maxSlide / slide;
  p.pos.x = rag.origin.x + dx * k;
  p.pos.z = rag.origin.z + dz * k;
}

export function ragdollHips(rag: EnemyRagdoll): THREE.Vector3 {
  const i = rag.index.get('Hips');
  return rag.particles[i ?? 0]?.pos ?? rag.particles[0].pos;
}

/** Last-resort lie-down so a failed ragdoll never leaves a standing or missing body. */
export function collapseEnemyVisual(model: THREE.Object3D, dir: THREE.Vector3, floorY: number): void {
  const bones = model.userData.procBones as Record<string, THREE.Object3D> | undefined;
  if (!bones?.Hips) {
    model.rotation.order = 'YXZ';
    model.rotation.x = Math.PI * 0.5;
    model.position.y = floorY + 0.18;
    return;
  }
  for (const b of Object.values(bones)) {
    const q = b.userData.bindQ as THREE.Quaternion | undefined;
    const p = b.userData.bindP as THREE.Vector3 | undefined;
    if (q) b.quaternion.copy(q);
    if (p) b.position.copy(p);
  }
  // Keep bind limb twists (legs/arms authored at rot.x = π). Overwriting those
  // to ~0 inverted the boxes and hid FrontSide faces.
  const yaw = Math.atan2(-dir.x, -dir.z);
  bones.Hips.position.y = 0.2;
  bones.Hips.rotation.order = 'YXZ';
  bones.Hips.rotation.set(Math.PI * 0.5, yaw, 0);
  bones.Spine?.rotateX(0.12);
  bones.Spine1?.rotateX(0.08);
}
